import { useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import { useSearchParams } from 'react-router-dom';

/**
 * UTM and referral tracking parameters for signup
 */
export interface SignupTrackingData {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    ref_user?: string;
    discount_code?: string;
    explicit_trial_days?: number;
}

const URL_PARAMS = {
    UTM_SOURCE: 'utm_source',
    UTM_MEDIUM: 'utm_medium',
    UTM_CAMPAIGN: 'utm_campaign',
    REF: 'ref',
    TRIAL_DAYS: 'trialDays',
    TRIAL_DAYS_UNDERSCORE: 'trial_days',
    SHOW_PROMO: 'show_promo',
    PROMO: 'promo',
    DISCOUNT_CODE: 'code',
} as const;

/**
 * Cookie names for tracking data
 * Matches Vue implementation in Auth.js store
 */
const TRACKING_COOKIES = {
    UTM_SOURCE: 'utm_source',
    UTM_MEDIUM: 'utm_medium',
    UTM_CAMPAIGN: 'utm_campaign',
    REF: 'ref',
    DISCOUNT_CODE: 'discount_code',
    TRIAL_DAYS: 'trial_days',
    SHOW_PROMO: 'show_promo',
} as const;

/**
 * Session storage keys for tracking data
 * Matches Vue implementation in Auth.js SAVE_SESSION_METADATA
 */
const SESSION_KEYS = {
    UTM_SOURCE: 'utm_source',
    UTM_MEDIUM: 'utm_medium',
    UTM_CAMPAIGN: 'utm_campaign',
    REF: 'ref',
} as const;

/**
 * Domains to set cookies on (matches Vue implementation)
 */
const COOKIE_DOMAINS = ['stanwith.me', 'stan.store'] as const;

/**
 * Sets a cookie with the given name and value
 * Matches Vue's SAVE_SIGN_UP_METADATA behavior - cookies without explicit expiry
 */
const setTrackingCookie = (name: string, value: string) => {
    // Vue's saveSignUpMetadata sets cookies without explicit expiry (session cookies)
    Cookies.set(name, value, { path: '/' });
};

/**
 * Sets a cookie across all domains (for trial_days)
 * Matches Vue's trackTraffic behavior for trial_days
 */
const setTrackingCookieAllDomains = (name: string, value: string, expiryDays: number) => {
    // Set for current domain
    Cookies.set(name, value, { expires: expiryDays, path: '/' });

    // Set for all specified domains (matches Vue behavior)
    COOKIE_DOMAINS.forEach(domain => {
        Cookies.set(name, value, { expires: expiryDays, domain, path: '/' });
    });
};

/**
 * Gets a tracking value from URL params, session storage, or cookies (in that order of priority)
 */
const getTrackingValue = (
    urlParams: URLSearchParams,
    paramName: string,
    sessionKey: string,
    cookieName: string
): string | undefined => {
    // Priority: URL params > Session storage > Cookies
    const urlValue = urlParams.get(paramName);
    if (urlValue) return urlValue;

    const sessionValue = sessionStorage.getItem(sessionKey);
    if (sessionValue) return sessionValue;

    const cookieValue = Cookies.get(cookieName);
    if (cookieValue) return cookieValue;

    return undefined;
};

/**
 * Hook to capture and persist UTM and referral tracking data from URL parameters
 *
 * This hook:
 * 1. Reads UTM parameters (utm_source, utm_medium, utm_campaign) from URL
 * 2. Reads referral code (ref) from URL
 * 3. Persists these values to cookies and session storage for later use
 * 4. Returns the tracking data for use in registration payload
 *
 * @example
 * ```tsx
 * const { trackingData, captureFromUrl } = useSignupTracking();
 *
 * // Use in registration payload
 * const payload = {
 *   ...formData,
 *   ...trackingData,
 * };
 * ```
 */
export function useSignupTracking() {
    const [searchParams] = useSearchParams();

    // Capture tracking data from URL on mount and persist to cookies/session
    useEffect(() => {
        const utmSource = searchParams.get(URL_PARAMS.UTM_SOURCE);
        const utmMedium = searchParams.get(URL_PARAMS.UTM_MEDIUM);
        const utmCampaign = searchParams.get(URL_PARAMS.UTM_CAMPAIGN);
        const ref = searchParams.get(URL_PARAMS.REF);
        // Support both trialDays (camelCase) and trial_days (underscore) URL params
        const trialDays = searchParams.get(URL_PARAMS.TRIAL_DAYS) || searchParams.get(URL_PARAMS.TRIAL_DAYS_UNDERSCORE);
        const showPromo = searchParams.get(URL_PARAMS.SHOW_PROMO) || searchParams.get(URL_PARAMS.PROMO);
        const discountCode = searchParams.get(URL_PARAMS.DISCOUNT_CODE);

        // Persist UTM parameters to cookies and session storage
        if (utmSource) {
            setTrackingCookie(TRACKING_COOKIES.UTM_SOURCE, utmSource);
            sessionStorage.setItem(SESSION_KEYS.UTM_SOURCE, utmSource);
        }
        if (utmMedium) {
            setTrackingCookie(TRACKING_COOKIES.UTM_MEDIUM, utmMedium);
            sessionStorage.setItem(SESSION_KEYS.UTM_MEDIUM, utmMedium);
        }
        if (utmCampaign) {
            setTrackingCookie(TRACKING_COOKIES.UTM_CAMPAIGN, utmCampaign);
            sessionStorage.setItem(SESSION_KEYS.UTM_CAMPAIGN, utmCampaign);
        }
        if (ref) {
            setTrackingCookie(TRACKING_COOKIES.REF, ref);
            sessionStorage.setItem(SESSION_KEYS.REF, ref);
        }

        // Handle trial days (only allow 14 or 30)
        // Vue sets trial_days with 7 day expiry across all domains
        if (trialDays) {
            const parsedTrialDays = parseInt(trialDays, 10);
            if (parsedTrialDays === 14 || parsedTrialDays === 30) {
                setTrackingCookieAllDomains(TRACKING_COOKIES.TRIAL_DAYS, parsedTrialDays.toString(), 7);
            }
        }

        // Handle promo/discount
        if (showPromo) {
            setTrackingCookie(TRACKING_COOKIES.SHOW_PROMO, 'true');
        }
        if (discountCode) {
            setTrackingCookie(TRACKING_COOKIES.DISCOUNT_CODE, discountCode);
        }
    }, [searchParams]);

    // Build tracking data from all sources (URL, session, cookies)
    const trackingData = useMemo((): SignupTrackingData => {
        const data: SignupTrackingData = {};

        // Get UTM parameters
        const utmSource = getTrackingValue(
            searchParams,
            URL_PARAMS.UTM_SOURCE,
            SESSION_KEYS.UTM_SOURCE,
            TRACKING_COOKIES.UTM_SOURCE
        );
        if (utmSource) data.utm_source = utmSource;

        const utmMedium = getTrackingValue(
            searchParams,
            URL_PARAMS.UTM_MEDIUM,
            SESSION_KEYS.UTM_MEDIUM,
            TRACKING_COOKIES.UTM_MEDIUM
        );
        if (utmMedium) data.utm_medium = utmMedium;

        const utmCampaign = getTrackingValue(
            searchParams,
            URL_PARAMS.UTM_CAMPAIGN,
            SESSION_KEYS.UTM_CAMPAIGN,
            TRACKING_COOKIES.UTM_CAMPAIGN
        );
        if (utmCampaign) data.utm_campaign = utmCampaign;

        // Get referral code
        const refUser = getTrackingValue(searchParams, URL_PARAMS.REF, SESSION_KEYS.REF, TRACKING_COOKIES.REF);
        if (refUser) data.ref_user = refUser;

        // Get discount code (only if show_promo is set)
        const showPromo = Cookies.get(TRACKING_COOKIES.SHOW_PROMO) === 'true';
        const discountCode = Cookies.get(TRACKING_COOKIES.DISCOUNT_CODE);
        if (showPromo && discountCode) {
            data.discount_code = discountCode;
        }

        // Get explicit trial days - support both trialDays (camelCase) and trial_days (underscore) URL params
        const trialDaysStr =
            searchParams.get(URL_PARAMS.TRIAL_DAYS) ||
            searchParams.get(URL_PARAMS.TRIAL_DAYS_UNDERSCORE) ||
            Cookies.get(TRACKING_COOKIES.TRIAL_DAYS);
        if (trialDaysStr) {
            const trialDays = parseInt(trialDaysStr, 10);
            if (trialDays === 14 || trialDays === 30) {
                data.explicit_trial_days = trialDays;
            }
        }

        return data;
    }, [searchParams]);

    return {
        /**
         * Tracking data ready to be spread into registration payload
         */
        trackingData,

        /**
         * Individual tracking values for display or conditional logic
         */
        utmSource: trackingData.utm_source,
        utmMedium: trackingData.utm_medium,
        utmCampaign: trackingData.utm_campaign,
        refUser: trackingData.ref_user,
        discountCode: trackingData.discount_code,
        explicitTrialDays: trackingData.explicit_trial_days,

        /**
         * Whether the user came from a referral link
         */
        hasReferral: !!trackingData.ref_user,

        /**
         * Whether the user has any UTM tracking
         */
        hasUtmTracking: !!(trackingData.utm_source || trackingData.utm_medium || trackingData.utm_campaign),
    };
}
